/* What this salon has bought, as far as the console is concerned.
 *
 * The board is a product now rather than one salon's tool, and a product has
 * customers on different plans. This is the one place that knows which, and it
 * answers exactly one question for the rest of the app:
 *
 *     licensing.licensed('appointments')  ->  true | false
 *
 * The decision itself is in js/rules/licence.js, pure and tested, because this
 * file cannot be imported without a Supabase config and a window — the same
 * split, for the same reason, as js/rules/queue.js and sync.js. What is left
 * here is the wiring: asking, caching, and re-asking.
 *
 * ── THIS IS NOT A SECURITY BOUNDARY, AND MUST NOT BE MISTAKEN FOR ONE ───────
 *
 * What stops one salon reading another's rows is RLS, in the database, where a
 * browser cannot reach it. What this does is decide which SCREENS a salon is
 * offered — a commercial control, enforced in a bundle that ships to every
 * tablet and could be edited by anybody who wanted to badly enough. That is a
 * deliberate and acceptable position, and js/rules/licence.js states the
 * argument in full: this fails open, always, on every route where the answer is
 * not known.
 *
 * ── WHAT IT ASKS ────────────────────────────────────────────────────────────
 *
 * `tenant_entitlements()` — no arguments, so it can only ever answer about the
 * caller, and it resolves through `private.license_resolve()`, the single
 * implementation of the rule in the database. See
 * admin/server/schema/14-licensing.sql. A project without that file applied
 * answers an error, and an error changes nothing.
 *
 * The cache is what makes "offline" work and is namespaced per tenant, like the
 * write queue and the settings mirror, for the reason tenant.js gives: a cached
 * licence read under another salon's name would be one business's plan applied
 * to another's console.
 *
 * It also records WHO it was read for. Since admin/server/schema/15-roles.sql
 * the answer is the salon's licence narrowed by the caller's role, so two
 * people at one salon can get different answers — the owner's Reports must not
 * be what the front desk boots into after the owner signs out. The cache is
 * still applied at boot, before the session is restored (the last person on a
 * station is the right guess, as tenant.js argues for the tenant); it is thrown
 * away the moment the session turns out to be somebody else.
 */
import * as Vue from 'vue';
import { licence } from './rules/licence.js';
import { sync } from './sync.js';
import { tenant } from './tenant.js';

var CACHE_BASE = 'dreamspa.dash.licence.v1';

/* Re-asked every quarter of an hour, not only at boot.
 *
 * A station tablet is opened on Monday and not reloaded until something goes
 * wrong. Without this, a licence granted at eleven would not reach the salon
 * that phoned about it at ten to, and a term that ended at midnight would leave
 * the board up until somebody happened to refresh. Fifteen minutes is far
 * inside the granularity a licence is actually sold at, and it is one small
 * request.
 */
var REFRESH_MS = 900000;

var state = Vue.reactive(licence.empty());

function adopt(next) {
  if (!next) return false;
  state.known = next.known;
  state.planId = next.planId;
  state.planName = next.planName;
  state.features = next.features;
  state.seats = next.seats;
  state.expiresAt = next.expiresAt;
  state.status = next.status;
  return true;
}

function readRaw() {
  try {
    var raw = window.localStorage.getItem(tenant.ns(CACHE_BASE));
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

function readCache() { return licence.fromCache(readRaw()); }

// Whose answer the cache holds. Null for one written before roles existed,
// which is treated as somebody else's — it may have been the owner's.
function cachedUser() {
  var raw = readRaw();
  return raw && raw.userId ? raw.userId : null;
}

function writeCache() {
  try {
    window.localStorage.setItem(tenant.ns(CACHE_BASE), JSON.stringify({
      userId: (sync.auth.user && sync.auth.user.id) || null,
      planId: state.planId,
      planName: state.planName,
      features: state.features,
      seats: state.seats,
      expiresAt: state.expiresAt,
      status: state.status
    }));
  } catch (e) { /* full or unavailable — the next load asks the server anyway */ }
}

// The cached answer, applied before anything is asked. Same reasoning as the
// board cache and the settings mirror: a station that has been signed in before
// should come up with the right modules on it rather than with all of them for
// the first second and then fewer.
adopt(readCache());

/* Ask the database.
 *
 * Three outcomes, and the difference between the last two is the whole of the
 * fail-open behaviour:
 *
 *   a row      the answer. Applied and cached.
 *   no rows    this session resolves to no salon — signed out, or a suspended
 *              one. Nothing is changed: a suspended salon reads nothing anyway,
 *              and a signed-out one is looking at a cache.
 *   an error   the function is not there, or the network is not. Nothing is
 *              changed, and if nothing was ever known, everything stays
 *              licensed.
 */
async function load() {
  try {
    var res = await sync.sb.rpc('tenant_entitlements');
    if (res.error) return false;
    var next = licence.fromRow((res.data && res.data[0]) || null);
    if (!adopt(next)) return false;
    writeCache();
    return true;
  } catch (e) {
    return false;
  }
}

// The question the rest of the console asks. See js/rules/licence.js — an
// unknown state licenses everything (except `dreamcloud` and `optimizer`, the
// modules held back until the answer is known), and that is the point of the file.
function licensed(feature) {
  return licence.entitles(state, feature);
}

// For the screens that have to explain themselves rather than just switch off.
var summary = Vue.computed(function () {
  return {
    known: state.known,
    status: state.status,
    planName: state.planName,
    // A salon that has never been granted anything and one whose term ran out
    // want different sentences — the second keeps naming the plan it had.
    lapsed: state.status === 'lapsed',
    unlicensed: state.known && state.status !== 'active'
  };
});

/* Re-asked when the session changes, and when the tenant does.
 *
 * The tenant is the interesting one: `resolveTenant()` in sync.js can move this
 * browser to a different salon after the fact — on a first sign-in, or when a
 * platform admin's support session resolves to somebody else's salon — and the
 * cache key moves with it. Reading the new namespace and re-asking is how the
 * console ends up showing the salon it is actually signed in as rather than the
 * modules of the one before it.
 */
Vue.watch(function () {
  return [sync.auth.user && sync.auth.user.id, sync.auth.tenantId];
}, function (next, previous) {
  if (previous && next[1] !== previous[1]) adopt(readCache());
  // Another person's answer is not this person's. Back to unknown — which
  // offers everything for the round trip until load() answers, the same as a
  // first boot — rather than leaving one role's screens up for another. Their
  // data is the database's to withhold either way (15-roles.sql).
  if (next[0] && cachedUser() !== next[0]) adopt(licence.empty());
  if (sync.auth.user) load();
}, { immediate: true });

setInterval(function () {
  if (sync.auth.user) load();
}, REFRESH_MS);

export const licensing = {
  state: state,
  summary: summary,
  licensed: licensed,
  load: load,
  REFRESH_MS: REFRESH_MS
};
